/**
 * clientRecordEditApi — the FE transport for the record page's direct
 * edit / archive / restore affordances (the #408 writer routes; this is
 * the FE-integration swap that retires cardSpine's stubbedProjectionWrite).
 *
 *   PATCH /api/t/{slug}/v1/clients/{clientId}/record/{projection}/{rowId}
 *     body { action:'edit', fields:{ <canonical registry keys> } }
 *          | { action:'archive' } | { action:'restore' }
 *   POST  /api/t/{slug}/v1/clients/{clientId}/record/{projection}
 *     body { fields:{ <canonical registry keys> } }  (direct add)
 *   (Housing, 0148 / D9: projection 'housing' on these routes — the old
 *    single-value /record/living-situation route is retired.)
 *
 * RECORD_EDIT_PROJECTIONS below is the FE hand-mirror of the API's
 * DIRECT_EDIT_PROJECTIONS map (api/_shared/repos/_noteProjections.ts) —
 * every projection path token the PATCH route accepts, each carrying the
 * registry spec type its editable-field allowlist derives from (via
 * @/lib/noteVocabulary — ONE registry, never a hand list of fields) plus
 * the projection's PAYLOAD-ONLY display fields. Payload-only fields have
 * no projection column: the aggregate read derives them from the row's
 * source-event payload, so an edit RESUBMITS their current values
 * alongside the changed fields — otherwise they would vanish when the
 * row's provenance re-points to the new direct_edit event.
 *
 * hospital-events takes edit, archive and restore (LP-19); its edit may
 * re-pick the facility's Directory organization beside the name.
 * Providers JOINED the edit map at WS-5 (full
 * edit + archive on the row's own content columns); linkProviderRow
 * below stays their separate narrow action (directory care-web,
 * 2026-07-10): attaching a Directory identity to an UNLINKED row —
 * never a content edit.
 *
 * Fetch semantics mirror src/lib/notes.ts (redirect:'manual' on writes
 * for the X.5.4 auth-expiry signal; ApiError normalization).
 */

import { writeFetch } from './writeFetch';
import { ApiError } from './api';
import {
  contactSentence,
  errorCodeOf,
  fieldOf,
  serverSentence,
  sessionExpiredText,
  type SupportContact,
} from './errorCopy';
import { FIELD_ERROR_FALLBACK, fieldSentence, ruleFromSpec, type FieldRule } from './fieldErrorCopy';
import { fieldLabel, humanizeToken } from './fieldLabels';
import type { NoteProposalType, PayloadFieldSpec } from './noteVocabulary';

// ---------------------------------------------------------------------
// The projection map — hand-mirror of the API's DIRECT_EDIT_PROJECTIONS
// ---------------------------------------------------------------------

export interface RecordEditProjectionConfig {
  /** Registry type whose spec derives the editable fields. */
  specType: NoteProposalType;
  /** Registry keys with NO projection column (sourced from the event
   *  payload on read) — resubmitted with every edit so they survive the
   *  source-event re-point. */
  payloadOnly: readonly string[];
}

export const RECORD_EDIT_PROJECTIONS = {
  medications: { specType: 'medication_change', payloadOnly: ['prescriber'] },
  conditions: { specType: 'condition_change', payloadOnly: [] },
  // The eleventh projection (0136, WS-3): target-row class like
  // medications — every registry field has a column.
  allergies: { specType: 'allergy_change', payloadOnly: [] },
  contacts: { specType: 'contact_change', payloadOnly: [] },
  // appointment_past/_upcoming share one spec; either derives the same
  // allowlist (the API map uses appointment_past too).
  appointments: { specType: 'appointment_past', payloadOnly: ['provider', 'purpose', 'date_text'] },
  'risk-flags': { specType: 'risk_change', payloadOnly: [] },
  'legal-instruments': { specType: 'directive_change', payloadOnly: [] },
  coverage: { specType: 'coverage_change', payloadOnly: [] },
  // The tenth projection (0122): register class — every registry field
  // (activity / level / note) has a column, so no payload-only riders.
  'functional-status': { specType: 'functional_status_change', payloadOnly: [] },
  // WS-5 (0140): target-row class. The allowlist derives from
  // PROVIDER_SPEC minus its directoryOnly fields (specialty/organization
  // render from the linked Directory entry — no per-client column) and
  // minus the uuid arms (link-flow-only), leaving display_name / role /
  // office_phone / office_address — all real columns, no payload riders.
  providers: { specType: 'provider_new', payloadOnly: [] },
  // LP-19: target-row class. date_text is the payload-only display field
  // (only ever note-extracted), resubmitted with every edit so a stay known
  // by its words keeps them.
  'hospital-events': { specType: 'hospital_event', payloadOnly: ['date_text'] },
} as const satisfies Record<string, RecordEditProjectionConfig>;

export type RecordEditProjection = keyof typeof RECORD_EDIT_PROJECTIONS;

/**
 * The direct-ADD surfaces (record-add slice) — FE hand-mirror of the
 * API's DIRECT_ADD_PROJECTIONS. Identical membership to the edit map
 * above: a provider add = joining the client's care team of providers,
 * Directory link first, unlinked-by-name fallback — the CM explicitly
 * picks, so the directory uuid arms are allowed on that one ADD surface.
 * Hospital events add like every other projection (founder polish
 * 2026-07-06, closing the #430 gap: the card previously filled from
 * notes only), and take the edit too since LP-19.
 */
export const RECORD_ADD_PROJECTIONS = {
  ...RECORD_EDIT_PROJECTIONS,
} as const satisfies Record<string, RecordEditProjectionConfig>;

export type RecordAddProjection = keyof typeof RECORD_ADD_PROJECTIONS;

// ---------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------

export type RecordRowPatchBody =
  | { action: 'edit'; fields: Record<string, unknown> }
  | { action: 'archive' }
  | { action: 'restore' };

export interface RecordRowEditResult {
  table: string;
  /** The RESULTING row id — may DIFFER from addressed_row_id after a
   *  register natural-key change (archive-old + upsert-new). */
  row_id: string;
  addressed_row_id?: string;
  is_active: boolean;
  /** null on an idempotent archive/restore no-op. */
  record_event_id: string | null;
  row?: Record<string, unknown>;
}

// ---------------------------------------------------------------------
// Transport (notes.ts PATCH semantics)
// ---------------------------------------------------------------------

async function sendJson<T>(
  method: 'PATCH' | 'POST',
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await writeFetch(path, {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
  });
  if (!res.ok) {
    let errBody: unknown;
    try {
      errBody = await res.json();
    } catch {
      errBody = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, errBody, `${method} ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

function patchJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
  return sendJson<T>('PATCH', path, body);
}

function recordBase(slug: string, clientId: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/record`;
}

/** Edit / archive / restore one projection row. */
export function patchRecordRow(
  slug: string,
  clientId: string,
  projection: RecordEditProjection,
  rowId: string,
  body: RecordRowPatchBody,
): Promise<RecordRowEditResult> {
  return patchJson<RecordRowEditResult>(
    `${recordBase(slug, clientId)}/${projection}/${encodeURIComponent(rowId)}`,
    body as unknown as Record<string, unknown>,
  );
}

/** Direct ADD of one projection row (record-add slice): POST the
 *  projection route with { fields } — no row id. 201 echoes the created
 *  row identity; callers refetch the aggregate (one audited read). */
export function postRecordRow(
  slug: string,
  clientId: string,
  projection: RecordAddProjection,
  fields: Record<string, unknown>,
): Promise<RecordRowEditResult> {
  return sendJson<RecordRowEditResult>(
    'POST',
    `${recordBase(slug, clientId)}/${projection}`,
    { fields },
  );
}

export interface ProviderLinkResult {
  table: 'client_providers';
  row_id: string;
  record_event_id: string;
  directory_individual_id: string | null;
  directory_org_id: string | null;
}

/**
 * Link an UNLINKED care-team row to a directory entry (directory
 * care-web, founder feedback 2026-07-10) — the provider-specific PATCH
 * action beside the standard edit/archive/restore (WS-5). Exactly one
 * arm travels. 409 provider_already_linked when the row already carries
 * an arm (refresh the record).
 */
export function linkProviderRow(
  slug: string,
  clientId: string,
  rowId: string,
  arm: { directory_individual_id: string } | { directory_org_id: string },
): Promise<ProviderLinkResult> {
  return patchJson<ProviderLinkResult>(
    `${recordBase(slug, clientId)}/providers/${encodeURIComponent(rowId)}`,
    { action: 'link', ...arm },
  );
}

export interface AllergiesNoneKnownResult {
  allergies_none_known_at: string | null;
}

/** The NKDA marker (0137 §5 — founder ruling 2026-07-10): set/clear the
 *  explicit "no known allergies" confirmation. The server refuses a set
 *  while active allergy rows exist (409 allergies_on_file — mutually
 *  exclusive by construction). */
export function postAllergiesNoneKnown(
  slug: string,
  clientId: string,
  noneKnown: boolean,
): Promise<AllergiesNoneKnownResult> {
  return sendJson<AllergiesNoneKnownResult>(
    'POST',
    `${recordBase(slug, clientId)}/allergies-none-known`,
    { none_known: noneKnown },
  );
}

export interface TransportationConsentEditResult {
  transportation_consent: string;
  record_event_id: string;
}

/** The 0127 consent column (the second [M3]-shaped exception) — no row
 *  id, its own route; body carries exactly { transportation_consent }. */
export function patchTransportationConsent(
  slug: string,
  clientId: string,
  value: string,
): Promise<TransportationConsentEditResult> {
  return patchJson<TransportationConsentEditResult>(
    `${recordBase(slug, clientId)}/transportation-consent`,
    { transportation_consent: value },
  );
}

// ---------------------------------------------------------------------
// Error copy for a failed record write
// ---------------------------------------------------------------------

/** What the dialog sent, so a refused field reads one plain sentence:
 *  the fields' registry specs, the values the dialog held for them, the
 *  labels its boxes show where they are not fieldLabel, and the words its
 *  choices show where they are not humanizeToken. */
export interface RecordEditErrorContext {
  specs: Readonly<Record<string, PayloadFieldSpec>>;
  sent: Readonly<Record<string, unknown>>;
  labels?: Readonly<Record<string, string>>;
  valueLabels?: Readonly<Record<string, Readonly<Record<string, string>>>>;
}

function own<T>(map: Readonly<Record<string, T>> | undefined, key: string): T | undefined {
  return map !== undefined && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;
}

/** The sentence for one refused field, or null when the dialog holds no
 *  rule for it it can put in words. */
function refusedFieldSentence(field: string, ctx: RecordEditErrorContext): string | null {
  const spec = own(ctx.specs, field);
  if (spec === undefined) return null;
  const baseRule = ruleFromSpec(spec);
  if (baseRule === null) return null;
  const words = own(ctx.valueLabels, field);
  const wordOf = (raw: string): string => own(words, raw) ?? humanizeToken(raw);
  let rule: FieldRule = baseRule;
  let sent = own(ctx.sent, field);
  if ((rule.kind === 'one_of' || rule.kind === 'some_of') && spec.values) {
    rule = { kind: rule.kind, values: spec.values.map(wordOf) };
  }
  if (rule.kind === 'some_of') {
    // The set travels as an array; a form holds it comma-joined.
    const raw = Array.isArray(sent) ? sent : typeof sent === 'string' ? sent.split(',').filter(Boolean) : [];
    sent = raw.map((v) => wordOf(String(v)));
  }
  const label = own(ctx.labels, field) ?? fieldLabel(field);
  return fieldSentence(label, rule, sent);
}

/** One sentence for a failed record write. A refused field reads one
 *  plain sentence from its label and rule (`ctx` null when the write sent
 *  no fields); the validator's words are never read. `contact` is the
 *  support contact the sentences that name one are filled from. */
export function recordEditErrorMessage(
  err: unknown,
  ctx: RecordEditErrorContext | null,
  contact: SupportContact,
): string {
  // A refusal the server wrote for the screen (the office sets who
  // receives the bill; a picked prescriber no longer available) shows as
  // the server's own sentence.
  const said = serverSentence(err, 'record_edits');
  if (said !== null) return said;
  switch (errorCodeOf(err)) {
    case 'invalid_value':
    case 'invalid_body': {
      const f = fieldOf(err);
      const sentence = f !== null && ctx !== null ? refusedFieldSentence(f, ctx) : null;
      return sentence ?? FIELD_ERROR_FALLBACK;
    }
    case 'not_on_care_team':
      return contactSentence(
        "You're not on this client's care team, so you can't change their record. Ask {contact_admin} to add you.",
        contact,
      );
    case 'record_row_not_found':
      return 'That entry is already gone. Reload the page to see the record as it is now.';
    case 'provider_already_linked':
      return 'This provider is already linked to the Directory. Refresh the record to see it.';
    case 'client_not_found':
      return "This client record can't be found. Go back to Clients and pick the client again.";
    case 'auth_expired':
      // The shared sentence: the dialog keeps what was typed, so it says
      // to sign in and try again (errorCopy.ts).
      return sessionExpiredText(err) ?? FIELD_ERROR_FALLBACK;
    default:
      return contactSentence(
        "That change didn't save, and nothing was changed. Try again, and if it happens again, tell {contact_system}.",
        contact,
      );
  }
}
